import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useRecordContext, useTranslate } from 'react-admin'
import { 
  IconButton, 
  Tooltip, 
  Box,
  InputAdornment,
} from '@mui/material'
import { History as HistoryIcon } from '@mui/icons-material'
import FieldAuditModal from './field.audit.modal'

// Функция для извлечения значения поля с учетом индексов массива
const extractFieldValue = (record: any, fieldName: string): any => {
  if (!record || !fieldName) return undefined
  
  // Проверяем, есть ли индекс массива в имени поля (например: equipment_ids[2])
  const arrayIndexMatch = fieldName.match(/^(.+)\[(\d+)\]$/)
  
  if (arrayIndexMatch) {
    const [, baseFieldName, indexStr] = arrayIndexMatch
    const index = parseInt(indexStr, 10)
    const arrayValue = record[baseFieldName]
    
    if (Array.isArray(arrayValue) && index >= 0 && index < arrayValue.length) {
      return arrayValue[index]
    }
    return undefined
  }
  
  // Обычное поле без индекса
  return record[fieldName]
}

interface WithFieldAuditProps {
  tableName: string
  fieldName: string
  fieldLabel?: string
  children: React.ReactElement
  position?: 'end' | 'start'
  variant?: 'icon' | 'button'
}

// HOC для добавления кнопки истории к любому полю
export const withFieldAudit = (WrappedComponent: React.ComponentType<any>) => {
  return (props: any & { 
    auditConfig?: {
      tableName: string
      fieldName: string
      fieldLabel?: string
      position?: 'end' | 'start'
    }
  }) => {
    const { auditConfig, ...restProps } = props
    
    if (!auditConfig) {
      return <WrappedComponent {...restProps} />
    }
    
    return (
      <WithFieldAudit {...auditConfig}>
        <WrappedComponent {...restProps} />
      </WithFieldAudit>
    )
  }
}

// Компонент-обёртка для добавления кнопки истории к полю
export const WithFieldAudit: React.FC<WithFieldAuditProps> = ({
  tableName,
  fieldName,
  fieldLabel,
  children,
  position = 'end',
  variant = 'icon'
}) => {
  const [modalOpen, setModalOpen] = useState(false)
  // Для SelectInput: корневой элемент инпута, куда будем порталить иконку (чтобы центрировать идеально)
  const [inputRootEl, setInputRootEl] = useState<HTMLElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const record = useRecordContext()
  const translate = useTranslate()

  const recordAny = ((record as any) ?? {}) as any
  const currentValue = extractFieldValue(recordAny, fieldName)
  const recordId = record ? String(recordAny.id) : ''
  
  // Улучшенная логика определения типа компонента
  const childType = (children.type as any)?.displayName || (children.type as any)?.name || ''
  const childProps = (children as any)?.props || {}
  
  // Проверяем также дочерние элементы (например, для ReferenceInput с AutocompleteInput внутри)
  const nestedChildType = (childProps.children?.type as any)?.displayName || 
                          (childProps.children?.type as any)?.name || ''
  
  // SelectInput: имеет choices или использует optionText без filterToQuery
  const isSelectInput = Boolean(
    childType === 'SelectInput' || 
    nestedChildType === 'SelectInput' ||
    childProps.choices !== undefined || 
    (childProps.optionText !== undefined && 
     childProps.filterToQuery === undefined && 
     nestedChildType !== 'AutocompleteInput')
  )
  
  // AutocompleteInput: имеет filterToQuery, или это сам AutocompleteInput компонент, или у родителя есть filterToQuery
  const isAutocompleteInput = Boolean(
    childType === 'AutocompleteInput' ||
    nestedChildType === 'AutocompleteInput' ||
    childProps.filterToQuery !== undefined ||
    // Проверяем, есть ли у дочернего элемента AutocompleteInput
    (childProps.children && 
     ((childProps.children.type as any)?.displayName === 'AutocompleteInput' || 
      (childProps.children.type as any)?.name === 'AutocompleteInput'))
  )


  // Создаём кнопку истории
  const HistoryButton = (
    <Tooltip title={translate('resources.audit.field_history', { fieldLabel: fieldLabel || fieldName })}>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation()
          setModalOpen(true)
        }}
        sx={{ 
          color: 'text.secondary',
          '&:hover': { 
            color: 'primary.main',
            backgroundColor: 'primary.50'
          }
        }}
      >
        <HistoryIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  )

  // Создаём безопасные InputProps для разных типов компонентов
  const createSafeInputProps = () => {
    const existingInputProps = children.props.InputProps || {}
    
    // Для SelectInput и AutocompleteInput не вмешиваемся в InputProps/endAdornment, чтобы не конфликтовать с resettable и стрелкой
    if (isSelectInput || isAutocompleteInput) {
      return existingInputProps
    }

    if (position === 'start') {
      return {
        ...existingInputProps,
        startAdornment: (
          <InputAdornment position="start">
            {HistoryButton}
            {existingInputProps.startAdornment}
          </InputAdornment>
        ),
      }
    }

    // Для end position
    if (position === 'end') {
      const newInputProps = { ...existingInputProps }
      
      // Если есть существующий endAdornment, комбинируем
      if (existingInputProps.endAdornment) {
        newInputProps.endAdornment = (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {existingInputProps.endAdornment}
            {HistoryButton}
          </Box>
        )
      } else {
        newInputProps.endAdornment = (
          <InputAdornment position="end">
            {HistoryButton}
          </InputAdornment>
        )
      }
      
      return newInputProps
    }

    return existingInputProps
  }

  // Создаём дополнительные props для отключения conflicting функций
  const createSafeProps = () => {
    // Не отключаем resettable - пользователи должны иметь возможность очистить поле
    return {}
  }

  // Клонируем дочерний элемент с безопасными InputProps и props
  const enhancedChild = React.cloneElement(children, {
    ...children.props,
    ...createSafeProps(),
    InputProps: createSafeInputProps(),
    // Добавляем стили для выравнивания позиции иконки истории в обычных полях
    sx: {
      ...(children.props.sx || {}),
      // Для обычных полей (не Select/Autocomplete) настраиваем отступы endAdornment
      ...(!isSelectInput && !isAutocompleteInput && position === 'end' ? {
        '& .MuiInputAdornment-positionEnd': {
          marginRight: '4px', // Приближаем иконку к правому краю, как в портале
        },
        '& .MuiInputAdornment-positionEnd .MuiIconButton-root': {
          marginRight: '0px', // Убираем дополнительные отступы самой иконки
          padding: '4px', // Такой же padding, как в портале
        }
      } : {})
    }
  })

  // Для SelectInput и AutocompleteInput добавляем иконку истории как часть поля, не меняя endAdornment
  // Сдвигаем стрелку/иконки чуть влево, чтобы справа поместилась наша иконка
  const selectSx = {
    '& .MuiInputBase-root, & .MuiOutlinedInput-root': {
      position: 'relative',
    },
    '& .MuiSelect-icon': {
      right: 36,
    },
    '& .MuiSelect-select': {
      paddingRight: '80px !important',
    },
    // Для AutocompleteInput - сдвигаем только стрелку влево, не трогая иконку очистки
    '& .MuiAutocomplete-endAdornment .MuiAutocomplete-popupIndicator': {
      marginRight: '32px', // Сдвигаем только стрелку открытия списка влево
    },
    '& .MuiAutocomplete-endAdornment .MuiAutocomplete-clearIndicator': {
      marginRight: '8px', // Небольшой отступ для иконки очистки от нашей иконки
    },
    '& .MuiAutocomplete-input': {
      paddingRight: '76px !important', // Достаточно места для стрелки и иконки истории
    },
  } as const

  const selectChild = (isSelectInput || isAutocompleteInput) && position === 'end'
    ? React.cloneElement(children, {
        ...children.props,
        sx: Array.isArray((children as any).props?.sx)
          ? [...(children as any).props.sx, selectSx]
          : [(children as any).props?.sx, selectSx],
      })
    : enhancedChild

  // После рендера ищем реальный корневой DOM узел поля, чтобы спозиционировать иконку строго по центру
  useEffect(() => {
    if ((isSelectInput || isAutocompleteInput) && containerRef.current) {
      // Для разных типов компонентов ищем соответствующие селекторы
      const selectors = [
        '.MuiOutlinedInput-root', 
        '.MuiInputBase-root',
        '.MuiAutocomplete-root .MuiInputBase-root', // Для AutocompleteInput
        '.MuiFormControl-root .MuiInputBase-root'
      ]
      
      let root: HTMLElement | null = null
      for (const selector of selectors) {
        root = containerRef.current.querySelector(selector) as HTMLElement | null
        if (root) break
      }
      
      setInputRootEl(root || null)
    } else {
      setInputRootEl(null)
    }
  }, [isSelectInput, isAutocompleteInput, children])

  if ((isSelectInput || isAutocompleteInput) && position === 'end') {
    return (
      <>
        <Box ref={containerRef} sx={{ position: 'relative', width: '100%' }}>
          {selectChild}
        </Box>
  {record && inputRootEl && createPortal(
          (
            <Box
              sx={{
                position: 'absolute',
                right: 8, // Позиция иконки истории у правого края
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                alignItems: 'center',
                zIndex: 2, // Увеличиваем z-index, чтобы иконка была поверх других элементов
                pointerEvents: 'auto', // Обеспечиваем возможность клика
              }}
            >
              <Tooltip title={translate('resources.audit.field_history', { fieldLabel: fieldLabel || fieldName })}>
                <IconButton
                  size="small"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.stopPropagation()
                    setModalOpen(true)
                  }}
                  sx={{
                    padding: '4px',
                    color: 'text.secondary',
                    '&:hover': { 
                      color: 'primary.main',
                      backgroundColor: 'primary.50'
                    }
                  }}
                >
                  <HistoryIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ),
          inputRootEl
        )}

        {record && (
          <FieldAuditModal
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            tableName={tableName}
            recordId={recordId}
            fieldName={fieldName}
            fieldLabel={fieldLabel}
            currentValue={currentValue}
          />
        )}
      </>
    )
  }

  return (
    <>
      {enhancedChild}
      
      {record && (
        <FieldAuditModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          tableName={tableName}
          recordId={recordId}
          fieldName={fieldName}
          fieldLabel={fieldLabel}
          currentValue={currentValue}
        />
      )}
    </>
  )
}

// Компонент для простого добавления кнопки истории рядом с полем
export const FieldWithAudit: React.FC<{
  tableName: string
  fieldName: string
  fieldLabel?: string
  children: React.ReactElement
  spacing?: number
}> = ({ tableName, fieldName, fieldLabel, children, spacing = 1 }) => {
  const [modalOpen, setModalOpen] = useState(false)
  const record = useRecordContext()
  const translate = useTranslate()

  if (!record) {
    return children
  }

  const currentValue = extractFieldValue(record, fieldName)
  const recordId = String(record.id)

  return (
    <Box 
      display="flex" 
      alignItems="center" 
      gap={0.5}
      sx={{ 
        '& > *:first-of-type': {
          flexGrow: 1
        }
      }}
    >
      {children}
      
      <Tooltip title={translate('resources.audit.field_history', { fieldLabel: fieldLabel || fieldName })}>
        <IconButton
          size="small"
          onClick={() => setModalOpen(true)}
          sx={{ 
            color: 'action.active',
            padding: '4px',
            '&:hover': { 
              color: 'action.hover',
              backgroundColor: 'action.hover',
            }
          }}
        >
          <HistoryIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      
      <FieldAuditModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        tableName={tableName}
        recordId={recordId}
        fieldName={fieldName}
        fieldLabel={fieldLabel}
        currentValue={currentValue}
      />
    </Box>
  )
}

export default WithFieldAudit
